import { useState, useEffect, useRef, useCallback } from 'react';
import { setAudioInputDevice } from '../lib/twilioVoice.js';

export const MIC_STORAGE_KEY = 'civix_audio_input_device';

export function useMicrophoneAuth({ autoStartMonitor = true } = {}) {
  const [permission, setPermission] = useState('checking'); // checking | granted | prompt | denied | unsupported
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(() => {
    try {
      return localStorage.getItem(MIC_STORAGE_KEY) || 'default';
    } catch {
      return 'default';
    }
  });
  const [audioLevel, setAudioLevel] = useState(0); // 0 to 100
  const [errorMsg, setErrorMsg] = useState(null);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const permissionStatusRef = useRef(null);

  // Helper to enumerate audio input devices
  const enumerateDevices = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return [];
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices
        .filter((d) => d.kind === 'audioinput')
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${i + 1}`,
          groupId: d.groupId,
        }));

      setDevices(audioInputs);

      // Verify currently selected device still exists
      const saved = localStorage.getItem(MIC_STORAGE_KEY);
      const exists = audioInputs.some((d) => d.deviceId === saved);
      const chosenId = exists ? saved : audioInputs[0]?.deviceId || 'default';

      setSelectedDeviceId(chosenId);
      try {
        localStorage.setItem(MIC_STORAGE_KEY, chosenId);
      } catch (_) {}

      // Inform WebRTC softphone driver if available
      setAudioInputDevice(chosenId).catch(() => {});

      return audioInputs;
    } catch {
      return [];
    }
  }, []);

  // Stop real-time audio visualizer monitor
  const stopAudioMonitor = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        audioContextRef.current.close();
      } catch (_) {}
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  // Start real-time audio visualizer monitor
  const startAudioMonitor = useCallback(async (deviceId) => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
    stopAudioMonitor();

    try {
      const targetDevice = deviceId && deviceId !== 'default' ? { exact: deviceId } : undefined;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: targetDevice ? { deviceId: targetDevice } : true,
      });
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const audioCtx = new AudioCtx();
      audioContextRef.current = audioCtx;

      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.4;
      analyserRef.current = analyser;

      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const avg = sum / (dataArray.length || 1);
        // Normalize 0-255 to roughly 0-100 with a slight boost for quiet voices
        const level = Math.min(100, Math.round((avg / 128) * 100));
        setAudioLevel(level);

        animFrameRef.current = requestAnimationFrame(tick);
      };

      tick();
    } catch {
      // Audio level visualizer failed non-fatally; dialer can still function
      setAudioLevel(0);
    }
  }, [stopAudioMonitor]);

  // Check initial permission status on mount
  const checkPermission = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      setPermission('unsupported');
      return 'unsupported';
    }

    try {
      if (navigator.permissions?.query) {
        try {
          const status = await navigator.permissions.query({ name: 'microphone' });
          permissionStatusRef.current = status;
          setPermission(status.state);

          // Listen for user changing permission in URL bar lock icon
          status.onchange = () => {
            setPermission(status.state);
            if (status.state === 'granted') {
              enumerateDevices();
              if (autoStartMonitor) {
                startAudioMonitor();
              }
            } else {
              stopAudioMonitor();
            }
          };

          if (status.state === 'granted') {
            await enumerateDevices();
            if (autoStartMonitor) {
              startAudioMonitor();
            }
          }
          return status.state;
        } catch {
          // Some browsers throw on { name: 'microphone' } query
        }
      }

      // Fallback: check device labels to infer if permission was previously granted
      const devs = await navigator.mediaDevices.enumerateDevices();
      const hasLabels = devs.some((d) => d.kind === 'audioinput' && d.label);
      if (hasLabels) {
        setPermission('granted');
        await enumerateDevices();
        if (autoStartMonitor) {
          startAudioMonitor();
        }
        return 'granted';
      }

      setPermission('prompt');
      return 'prompt';
    } catch (err) {
      setErrorMsg(err.message);
      setPermission('prompt');
      return 'prompt';
    }
  }, [enumerateDevices, autoStartMonitor, startAudioMonitor, stopAudioMonitor]);

  // Request permission explicitly (called on "Allow Microphone Access" click)
  const requestPermission = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPermission('unsupported');
      return { ok: false, reason: 'unsupported' };
    }

    try {
      setErrorMsg(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release initial probe stream immediately
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch (_) {}

      setPermission('granted');
      await enumerateDevices();
      if (autoStartMonitor) {
        startAudioMonitor();
      }
      return { ok: true };
    } catch (err) {
      const isDenied = err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError';
      setPermission(isDenied ? 'denied' : 'prompt');
      setErrorMsg(err.message || 'Microphone access was denied');
      stopAudioMonitor();
      return { ok: false, reason: err.name, error: err };
    }
  }, [enumerateDevices, autoStartMonitor, startAudioMonitor, stopAudioMonitor]);

  // Change selected microphone device
  const selectDevice = useCallback((deviceId) => {
    setSelectedDeviceId(deviceId);
    try {
      localStorage.setItem(MIC_STORAGE_KEY, deviceId);
    } catch (_) {}
    setAudioInputDevice(deviceId).catch(() => {});
    if (permission === 'granted' && autoStartMonitor) {
      startAudioMonitor(deviceId);
    }
  }, [permission, autoStartMonitor, startAudioMonitor]);

  useEffect(() => {
    checkPermission();
    return () => {
      stopAudioMonitor();
      if (permissionStatusRef.current) {
        permissionStatusRef.current.onchange = null;
      }
    };
  }, [checkPermission, stopAudioMonitor]);

  return {
    permission,          // 'checking' | 'granted' | 'prompt' | 'denied' | 'unsupported'
    isReady: permission === 'granted',
    devices,
    selectedDeviceId,
    audioLevel,
    errorMsg,
    requestPermission,
    recheckPermission: checkPermission,
    selectDevice,
    startAudioMonitor,
    stopAudioMonitor,
  };
}

export default useMicrophoneAuth;
